import { useLayoutEffect, useRef, useState } from 'react';

interface UseFitTextOptions {
  /** The text being rendered — re-fits when it changes. */
  text: string;
  /** Largest font size (px) — the natural size for short text. */
  maxPx: number;
  /** Smallest font size (px) — never shrink past this. */
  minPx: number;
  /** Line budget to use when the element carries no resolved
   *  `-webkit-line-clamp` (see `resolveMaxLines`). */
  maxLines: number;
  /** Line-height ratio (unitless) applied while measuring + rendering. */
  lineHeight: number;
}

/**
 * The element's effective line budget. The caller clamps the text with
 * `-webkit-line-clamp`, which may be driven by a Design Constructor knob
 * (`var(--co-…-maxLines, N)`), so the browser has already resolved the
 * operator's value for us. Reading it back keeps the shrink target and the
 * visual clip on ONE number instead of two that can drift apart. No clamp,
 * `none`, or a garbage override all parse to NaN → `fallback`.
 */
function resolveMaxLines(el: HTMLElement, fallback: number): number {
  const parsed = Number.parseInt(getComputedStyle(el).webkitLineClamp, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * Auto-fit text to a maximum number of lines. Renders at `maxPx` and, if the
 * text wraps past the line budget in its container, steps the font size down
 * (1px at a time) until it fits — so long card titles collapse to ≤ budget on
 * every width (mobile + desktop) instead of pushing the layout.
 *
 * Text that still doesn't fit at `minPx` is left to the caller's
 * `-webkit-line-clamp` to ellipsise. Without that backstop the loop simply
 * gives up at the floor and the extra lines spill: a 95-char card name
 * rendered five lines and buried the card below it.
 *
 * Re-measures when the container's WIDTH changes (rotation / breakpoint /
 * iframe resize) and when the line budget changes; ignores height changes so
 * the font tweak can't feed back into a measure loop.
 *
 * Attach the returned `ref` to the text element and apply `fontPx` +
 * `lineHeight` to its style.
 */
export function useFitText({
  text,
  maxPx,
  minPx,
  maxLines,
  lineHeight,
}: UseFitTextOptions): {
  ref: React.RefObject<HTMLElement | null>;
  fontPx: number;
} {
  const ref = useRef<HTMLElement | null>(null);
  const [fontPx, setFontPx] = useState(maxPx);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const fit = () => {
      const lines = resolveMaxLines(el, maxLines);

      // Measure with the clamp OFF. `-webkit-line-clamp` truncates the box,
      // so `scrollHeight` reports the ALREADY-CLIPPED height — the loop would
      // call it a fit at `maxPx` and ellipsise away most of the title. The
      // caller's value is restored below rather than removed: this IS React's
      // inline style declaration, and React won't rewrite a prop it considers
      // unchanged.
      const prevClamp = el.style.getPropertyValue('-webkit-line-clamp');
      el.style.setProperty('-webkit-line-clamp', 'unset');

      let size = maxPx;
      el.style.lineHeight = String(lineHeight);
      el.style.fontSize = `${size}px`;
      // `scrollHeight` is the wrapped block height at the current size;
      // `size * lineHeight * lines` is the tallest it may be.
      const overflows = () =>
        el.scrollHeight > Math.ceil(size * lineHeight * lines) + 1;
      while (size > minPx && overflows()) {
        size -= 1;
        el.style.fontSize = `${size}px`;
      }

      el.style.setProperty('-webkit-line-clamp', prevClamp);
      setFontPx(size);
    };

    fit();

    // The line budget is a Design Constructor knob written to `:root`'s inline
    // style. Constructor overlays bypass React entirely (see
    // `useChildListener`), so nothing re-renders when the operator changes it
    // — watch `:root` and re-fit only when the RESOLVED budget actually moves.
    // Every other knob mutates the same attribute, hence the guard.
    let prevLines = resolveMaxLines(el, maxLines);
    const budgetObserver = new MutationObserver(() => {
      const lines = resolveMaxLines(el, maxLines);
      if (lines === prevLines) return;
      prevLines = lines;
      fit();
    });
    budgetObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
    });

    const parent = el.parentElement;
    if (!parent) return () => budgetObserver.disconnect();

    let prevWidth = parent.clientWidth;
    const widthObserver = new ResizeObserver(() => {
      const w = parent.clientWidth;
      if (w === prevWidth) return; // width unchanged → don't re-fit
      prevWidth = w;
      fit();
    });
    widthObserver.observe(parent);
    return () => {
      budgetObserver.disconnect();
      widthObserver.disconnect();
    };
  }, [text, maxPx, minPx, maxLines, lineHeight]);

  return { ref, fontPx };
}
